import { useMemo, useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import interviewsService, { SendPublicInterview } from '@/services/interviews';
import { FetchApiIds, CandidateInterviewPageStages } from '@/types';
import { Paths } from '@/types/routes';
import CandidateTechnicalValidationInterviewComponent from './CandidateTechnicalValidationInterview.component';
import { ProjectType } from '../SubmittingTechnicalValidation';

export default function CandidateTechnicalValidationInterview(): JSX.Element {
  const toast = useToastContext();
  const { interviewId, candidateId } = useParams();

  const [interviewState, setInterviewState] = useState(CandidateInterviewPageStages.WELCOME);
  const [alertState, setAlertState] = useState(false);

  const [projectData, setProjectData] = useState<ProjectType | null>(null);

  const interviewMutation = useApiSubmit<void, SendPublicInterview>(
    interviewsService.sendCandidatePublicTechnicalInterview
  );

  const interviewResponse = useApiFetch(
    [FetchApiIds.PUBLIC_CANDIDATE_INTERVIEW, interviewId, candidateId],
    async () =>
      interviewsService.getCandidatePublicInterview({
        interviewId: interviewId as string,
        candidateId: candidateId as string,
      }),
    {
      enabled: !!interviewId && !!candidateId,
    }
  );

  useEffect(() => {
    const hasSubmittedTechnicalChallenge = interviewResponse.data?.hasSubmittedTechnicalChallenge;

    if (hasSubmittedTechnicalChallenge) {
      setInterviewState(CandidateInterviewPageStages.ENDING);
    }
  }, [interviewResponse.data?.hasSubmittedTechnicalChallenge]);

  const candidateName = useMemo(() => {
    const { firstName, lastName } = interviewResponse?.data?.person ?? {};

    return `${firstName} ${lastName}`;
  }, [interviewResponse?.data?.person]);

  const { interviewName, interviewDescription } = useMemo(() => {
    const { name, description } =
      interviewResponse?.data?.interviewTechnicalChallenge?.technicalChallenge ?? {};

    return {
      interviewName: name,
      interviewDescription: description,
    };
  }, [interviewResponse?.data?.interviewTechnicalChallenge?.technicalChallenge]);

  const onChangeInterviewState = (type: CandidateInterviewPageStages): void => {
    setInterviewState(type);
  };

  const onSubmitAnswers = (data: ProjectType): void => {
    setAlertState((prev) => !prev);
    setProjectData(data);
  };

  const interviewTechnicalChallengeId = useMemo(
    () => interviewResponse?.data?.interviewTechnicalChallenge?.id ?? '',
    [interviewResponse?.data?.interviewTechnicalChallenge?.id]
  );

  const onConfirmSubmitting = async (): Promise<void> => {
    try {
      const payload = new FormData();

      payload.append('technicalChallengeId', interviewTechnicalChallengeId ?? '');

      if (projectData) {
        payload.append('technicalDocumentTitle', projectData.technicalDesign.title);
        payload.append('technicalDocumentContent', projectData.technicalDesign.body);
        payload.append('technicalVideo', projectData.technicalVideo);
        payload.append('githubProjectUrl', projectData.githubProjectUrl);
      }

      await interviewMutation.mutateAsync({
        payload,
        interviewId: interviewId ?? '',
        interviewTechnicalChallengeId: interviewTechnicalChallengeId ?? '',
      });

      toast.showSuccessFeedback('Your project have been submited.');
      setAlertState(false);
      onChangeInterviewState(CandidateInterviewPageStages.ENDING);
    } catch {
      toast.showErrorFeedback('There was a problem to submit your project, try again later.');
    }
  };

  const onCancelSubmitting = (): void => {
    setAlertState(false);
    setProjectData(null);
  };

  const { isLoading, isError } = interviewResponse;

  if (isLoading) {
    return <Loading fullPage />;
  }

  if (isError) {
    return <Navigate to={Paths.NOT_FOUND} />;
  }

  return (
    <CandidateTechnicalValidationInterviewComponent
      isSubmitting={interviewMutation.isLoading}
      candidateName={candidateName}
      onChangeInterviewState={onChangeInterviewState}
      onSubmitProject={onSubmitAnswers}
      interviewState={interviewState}
      alertState={alertState}
      onConfirmSubmitting={onConfirmSubmitting}
      onCancelSubmitting={onCancelSubmitting}
      interviewDescription={interviewDescription}
      interviewName={interviewName}
    />
  );
}
